import type { AIAgentType } from '../types';

/**
 * Detecta si el visitante parece ser una IA, bot o automatización.
 * Devuelve el tipo de agente si se detecta, o null si parece humano.
 */
export function detectAIAgent(): { type: AIAgentType; userAgent: string } | null {
  if (typeof navigator === 'undefined') return null;

  const ua = navigator.userAgent || '';
  const type = getAgentType(ua);
  if (type) return { type, userAgent: ua };

  // Heurísticas adicionales (navegador headless suele ocultar o reducir propiedades)
  if (isHeadlessLike()) {
    return { type: 'suspected_bot', userAgent: ua };
  }

  return null;
}

function getAgentType(ua: string): AIAgentType | null {
  if (/GPTBot|ChatGPT-User|Claude-Web|Anthropic-AI|Google-Extended|Gemini|Bard|PerplexityBot|cohere-ai/i.test(ua)) {
    return 'ai_crawler';
  }
  if (/HeadlessChrome|PhantomJS|Puppeteer|Playwright|Selenium|webdriver|Electron/i.test(ua)) {
    return 'headless';
  }
  if (/bot|crawler|spider|scraper/i.test(ua)) {
    return 'automation';
  }
  return null;
}

/** Comprueba indicios de entorno headless/automatizado */
function isHeadlessLike(): boolean {
  try {
    // navigator.webdriver suele ser true en Puppeteer/Playwright/Selenium
    if ((navigator as { webdriver?: boolean }).webdriver) {
      return true;
    }
    // Algunos headless tienen muy pocos plugins o lenguajes
    const langs = navigator.languages;
    if (langs && langs.length === 0 && navigator.language === '') {
      return true;
    }
    // Chrome headless a veces reporta esto
    if (navigator.userAgent.indexOf('HeadlessChrome') !== -1) {
      return true;
    }
  } catch {
    // ignore
  }
  return false;
}

/** Cache del resultado por sesión para no repetir detección */
let cachedResult: { type: AIAgentType; userAgent: string } | null | false = false;

export function getCachedAIAgent(): { type: AIAgentType; userAgent: string } | null {
  if (cachedResult === false) {
    cachedResult = detectAIAgent();
  }
  return cachedResult;
}
